import { useEffect, useMemo, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { SearchableSelect } from '@/components/ui/searchable-select'
import { formsApi } from '@/api/formsApi'
import { capaTargetLabel } from '@/lib/capaTarget'
import type { CapaTarget } from '@/api/proceduresApi'
import type { FormDefinition } from '@/types/cgt'

/**
 * Raise a CAPA from the CAPA page.
 *
 * The primary route is the "+" menu on a procedure page — Create CAPA, pick a
 * CAPA form — which attaches the form to the run in front of you. This is the
 * same act for someone who started from the CAPA page instead, so it asks the
 * one extra thing that route already knows: which run.
 *
 * Deliberately NOT a "create a CAPA procedure" dialog. A CAPA is a form raised
 * inside a run, so there is no procedure to create and no template to pick.
 */
export function CreateCapaDialog({
  open,
  onClose,
  targets,
  onCreated,
}: {
  open: boolean
  onClose: () => void
  /** In-progress runs, from `GET /procedures/capa-targets`. */
  targets: CapaTarget[]
  onCreated: (result: { target: CapaTarget; submissionId: string }) => void
}) {
  const [targetId, setTargetId] = useState('')
  const [formId, setFormId] = useState('')
  const [forms, setForms] = useState<FormDefinition[]>([])
  const [loadingForms, setLoadingForms] = useState(false)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setTargetId('')
    setFormId('')
    setError(null)
    setLoadingForms(true)
    formsApi
      .listDefinitions({ category: 'capa', active: true })
      .then(({ items }) => setForms(items))
      .catch(() => setError('Could not load the CAPA forms.'))
      .finally(() => setLoadingForms(false))
  }, [open])

  const targetOptions = useMemo(
    () => targets.map(t => ({ value: t.id, label: capaTargetLabel(t) })),
    [targets],
  )
  const formOptions = useMemo(
    () => forms.map(f => ({ value: f.id, label: `${f.name} (V${f.version ?? 1})` })),
    [forms],
  )

  const target = targets.find(t => t.id === targetId)
  const canSubmit = !!target && !!formId && !saving

  async function submit() {
    if (!target || !formId) return
    setSaving(true)
    setError(null)
    try {
      const submission = await formsApi.createSubmission({
        formDefinitionId: formId,
        procedureId: target.id,
        // Must MATCH the run's patient, not the caller's idea of one: the server
        // refuses a submission whose MRN differs from its procedure's. A run with
        // no patient (an MES order, a kit build) sends none — never an empty
        // string, which the server reads as a supplied value and rejects as
        // empty (z8nrz7dyc2 replaced the old format rule with `@IsNotEmpty`).
        ...(target.patientMrn ? { patientMrn: target.patientMrn } : {}),
        // Ad hoc, so the predecessor gate does not apply. A CAPA is raised
        // because something went wrong; it cannot wait on the sequence.
        isAdHoc: true,
      })
      onCreated({ target, submissionId: submission.id })
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not raise that CAPA.')
    } finally {
      setSaving(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={o => { if (!o) onClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Create CAPA</DialogTitle>
          <DialogDescription>
            Pick the run, then the CAPA form to raise on it.
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          <div className='space-y-2'>
            <label className='text-sm font-medium'>
              Run <span className='text-red-600'>*</span>
            </label>
            <SearchableSelect
              value={targetId}
              onChange={setTargetId}
              options={targetOptions}
              placeholder={
                targets.length ? 'Select a transplant, kit run or MES order…' : 'No runs in progress'
              }
              disabled={!targets.length}
              emptyMessage='No matching runs.'
            />
            {/* Says why the list is what it is, rather than leaving a finished
                run's absence looking like a bug. */}
            <p className='text-xs text-muted-foreground'>Runs in progress only.</p>
          </div>

          <div className='space-y-2'>
            <label className='text-sm font-medium'>
              CAPA form <span className='text-red-600'>*</span>
            </label>
            <SearchableSelect
              value={formId}
              onChange={setFormId}
              options={formOptions}
              placeholder={
                loadingForms
                  ? 'Loading CAPA forms…'
                  : forms.length
                    ? 'Select a CAPA form…'
                    : 'No CAPA forms defined yet'
              }
              disabled={loadingForms || !forms.length}
              emptyMessage='No matching forms.'
            />
            {!loadingForms && forms.length === 0 && (
              // The list is a form CATEGORY, so the fix is authoring one, not
              // retrying — an empty dropdown would say nothing about that.
              <p className='text-xs text-muted-foreground'>
                Create a <span className='font-medium'>CAPA</span> form under Forms first.
              </p>
            )}
          </div>

          {error && <p className='text-sm font-medium text-red-600'>{error}</p>}
        </div>

        <DialogFooter>
          <Button variant='outline' onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={() => void submit()} disabled={!canSubmit}>
            {saving && <Loader2 className='mr-1.5 h-3.5 w-3.5 animate-spin' />}
            Create CAPA
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
